import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import Icon from "./Icon";
import DataTable from "./DataTable";
import ServerDetailModal from "./ServerDetailModal";
import ApplicationDetailModal from "./ApplicationDetailModal";
import SearchInput from "./SearchInput";
import FilterSelect from "./FilterSelect";
import {
  StatusBadge,
  getStatusStyle,
  getApplicationStatus,
  getServerStatus,
  getRouterStatus,
  isServiceRunning,
} from "./Badge";
import { getAssetCategory, formatCategoryLabel } from "../utils/assetTaxonomy";
import { useAssetStore } from "../store/useAssetStore";
import {
  fetchApplicationTree,
  fetchApplicationSummary,
} from "../modules/ops/services/CMDBService";

// Helper: Truncate string with ellipsis
function truncate(str, max = 20) {
  if (!str) return "";
  const s = String(str);
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

// Asset Category Styles for Infrastructure Nodes
export const ASSET_CATEGORY_STYLES = {
  server: {
    label: "Server Host",
    badge: "SERVER",
    color: "#2563EB",
    bg: "#EFF6FF",
    border: "#BFDBFE",
  },
  router: {
    label: "Network Router",
    badge: "ROUTER",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "#DDD6FE",
  },
  switch: {
    label: "Network Switch",
    badge: "SWITCH",
    color: "#9333EA",
    bg: "#FAF5FF",
    border: "#E9D5FF",
  },
  firewall: {
    label: "Firewall Appliance",
    badge: "FIREWALL",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
  },
  database: {
    label: "Database Cluster",
    badge: "DATABASE",
    color: "#0891B2",
    bg: "#ECFEFF",
    border: "#A5F3FC",
  },
  load_balancer: {
    label: "Load Balancer",
    badge: "LOAD BAL",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
  },
  storage: {
    label: "Storage System",
    badge: "STORAGE",
    color: "#4F46E5",
    bg: "#EEF2FF",
    border: "#C7D2FE",
  },
};

export function getCategoryStyle(cat) {
  const c = String(cat || "server")
    .toLowerCase()
    .trim();
  return (
    ASSET_CATEGORY_STYLES[c] || {
      label: formatCategoryLabel(c),
      badge: truncate(c.toUpperCase(), 9),
      color: "#475569",
      bg: "#F8FAFC",
      border: "#CBD5E1",
    }
  );
}

// Application Tier Styles
export const APPLICATION_TIER_STYLES = {
  app_root: {
    label: "Platform Root",
    badge: "PLATFORM",
    color: "#1D4ED8",
    bg: "#EFF6FF",
    border: "#BFDBFE",
  },
  sub_app: {
    label: "Microservice",
    badge: "MICRO SVC",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "#DDD6FE",
  },
  app_service: {
    label: "Shared Service / DB",
    badge: "SERVICE",
    color: "#0891B2",
    bg: "#ECFEFF",
    border: "#A5F3FC",
  },
};

// Sync Status and Health Score (Inactive is always 0, missing data is inactive)
function computeHealthAndStatus(rawStatus, rawHealth) {
  const statusStr = String(rawStatus || "inactive")
    .toLowerCase()
    .trim();
  const isExplicitInactive =
    statusStr === "inactive" ||
    statusStr === "decommissioned" ||
    statusStr === "offline" ||
    statusStr === "stopped" ||
    statusStr === "unknown";

  if (isExplicitInactive) {
    return { healthScore: 0, normalizedStatus: "inactive" };
  }

  const hasValidHealth =
    rawHealth !== undefined && rawHealth !== null && !isNaN(Number(rawHealth));

  const healthScore = hasValidHealth
    ? Math.max(0, Math.min(100, Number(rawHealth)))
    : 100;

  return { healthScore, normalizedStatus: "active" };
}

// Multi-Tier Force Layout Calculation
function computeInitialPositions(
  nodes,
  edges,
  width = 1000,
  height = 560,
  domain = "infrastructure",
) {
  const coords = {};
  if (!nodes || nodes.length === 0) return coords;

  const n = nodes.length;
  const centerX = width / 2;
  const centerY = height / 2;

  if (domain === "applications") {
    const roots = nodes.filter((node) => node.type === "app_root");
    const subApps = nodes.filter((node) => node.type === "sub_app");
    const services = nodes.filter(
      (node) => node.type === "app_service" || node.type === "service",
    );
    const otherNodes = nodes.filter(
      (node) =>
        node.type !== "app_root" &&
        node.type !== "sub_app" &&
        node.type !== "app_service" &&
        node.type !== "service",
    );

    // Group and sort services by parent affiliation (Sub-App vs Root vs Shared)
    const subAppIds = new Set(
      subApps
        .map((s) => s.id)
        .concat(subApps.map((s) => `app:${s.rawId || s.id}`))
        .concat(subApps.map((s) => String(s.rawId || s.id))),
    );
    const rootIds = new Set(
      roots
        .map((r) => r.id)
        .concat(roots.map((r) => `app:${r.rawId || r.id}`))
        .concat(roots.map((r) => String(r.rawId || r.id))),
    );

    const isSubAppSvc = (svc) => {
      const parents = svc.parent_ids || [];
      return parents.some((p) => subAppIds.has(String(p)));
    };
    const isRootSvc = (svc) => {
      const parents = svc.parent_ids || [];
      const rootsList = svc.root_ids || [];
      return (
        parents.some((p) => rootIds.has(String(p))) ||
        rootsList.some((r) => rootIds.has(String(r)))
      );
    };

    const subAppServices = [];
    const sharedServices = [];
    const rootServices = [];
    const otherServices = [];

    [...services, ...otherNodes].forEach((svc) => {
      const hasSub = isSubAppSvc(svc);
      const hasRoot = isRootSvc(svc);
      if (hasSub && !hasRoot) {
        subAppServices.push(svc);
      } else if (hasSub && hasRoot) {
        sharedServices.push(svc);
      } else if (hasRoot) {
        rootServices.push(svc);
      } else {
        otherServices.push(svc);
      }
    });

    const sortedSvcNodes = [
      ...subAppServices,
      ...sharedServices,
      ...rootServices,
      ...otherServices,
    ];

    const totalSvcs = sortedSvcNodes.length;
    const maxTierCount = Math.max(
      roots.length + subApps.length,
      totalSvcs,
      3,
    );
    const virtualW = Math.max(width, maxTierCount * 260 + 160);
    const vCenterX = virtualW / 2;

    // Tier 1: Platform Roots (Top layer, y = 80)
    if (roots.length === 1 && subApps.length > 0) {
      coords[roots[0].id] = {
        x: vCenterX + 120,
        y: 80,
      };
    } else {
      const rootCount = Math.max(roots.length, 1);
      const rootSpacing = Math.max(260, Math.min(320, (virtualW - 200) / rootCount));
      const rootStartX = vCenterX - ((rootCount - 1) * rootSpacing) / 2;
      roots.forEach((node, i) => {
        coords[node.id] = {
          x: rootStartX + i * rootSpacing,
          y: 80,
        };
      });
    }

    // Tier 2: Microservices & Sub-Applications (Middle layer, y = 230)
    if (subApps.length === 1) {
      coords[subApps[0].id] = {
        x: vCenterX - 240,
        y: 230,
      };
    } else if (subApps.length > 1) {
      const subCount = subApps.length;
      const subSpacing = Math.max(260, Math.min(300, (virtualW - 200) / subCount));
      const subStartX = vCenterX - ((subCount - 1) * subSpacing) / 2;
      subApps.forEach((node, i) => {
        coords[node.id] = {
          x: subStartX + i * subSpacing,
          y: 230,
        };
      });
    }

    // Tier 3: Shared Databases & Infrastructure Services (Bottom layer, y = 420)
    const svcCount = Math.max(totalSvcs, 1);
    const svcSpacing = Math.max(240, Math.min(280, (virtualW - 120) / svcCount));
    const svcStartX = vCenterX - ((svcCount - 1) * svcSpacing) / 2;
    sortedSvcNodes.forEach((node, i) => {
      coords[node.id] = {
        x: svcStartX + i * svcSpacing,
        y: 420,
      };
    });

    return coords;
  }

  // Domain === "infrastructure": Central Router Orbit + Outer Compute Orbit
  if (n === 1) {
    coords[nodes[0].id] = { x: centerX, y: centerY };
    return coords;
  }

  const routerNodes = nodes.filter(
    (node) =>
      node.asset_category === "router" ||
      node.asset_category === "switch" ||
      node.asset_category === "firewall" ||
      (node.label || "").toLowerCase().includes("gateway"),
  );
  const otherNodes = nodes.filter(
    (node) => !routerNodes.some((r) => r.id === node.id),
  );

  if (routerNodes.length > 0 && otherNodes.length > 0) {
    const routerRadius = Math.max(90, routerNodes.length * 50);
    routerNodes.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / routerNodes.length - Math.PI / 2;
      coords[node.id] = {
        x: centerX + routerRadius * Math.cos(angle),
        y: centerY + routerRadius * Math.sin(angle),
      };
    });

    const outerRadius = Math.max(250, Math.min(420, otherNodes.length * 60));
    otherNodes.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / otherNodes.length - Math.PI / 2;
      coords[node.id] = {
        x: centerX + outerRadius * Math.cos(angle),
        y: centerY + outerRadius * Math.sin(angle),
      };
    });
  } else {
    const radius = Math.max(200, Math.min(400, n * 60));
    nodes.forEach((node, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      coords[node.id] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
  }

  const iterations = 50;
  const minNodeDist = 240;

  for (let iter = 0; iter < iterations; iter++) {
    const tempDisp = {};
    nodes.forEach((node) => {
      tempDisp[node.id] = { dx: 0, dy: 0 };
    });

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const n1 = nodes[i];
        const n2 = nodes[j];
        const p1 = coords[n1.id];
        const p2 = coords[n2.id];
        if (!p1 || !p2) continue;
        let dx = p1.x - p2.x;
        let dy = p1.y - p2.y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;
        if (dist < minNodeDist) {
          const force = (minNodeDist - dist) * 0.35;
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          tempDisp[n1.id].dx += fx;
          tempDisp[n1.id].dy += fy;
          tempDisp[n2.id].dx -= fx;
          tempDisp[n2.id].dy -= fy;
        }
      }
    }

    nodes.forEach((node) => {
      const disp = tempDisp[node.id];
      if (!disp) return;
      coords[node.id].x += disp.dx;
      coords[node.id].y += disp.dy;
    });
  }

  return coords;
}

export function TopologyView({
  topology,
  servers: propServers,
  applications: propApplications,
  initialDomain = "infrastructure",
  healthField = "health_percent",
  onSelectServer,
  onSelectApplication,
}) {
  const [domain, setDomain] = useState(initialDomain); // "applications" | "infrastructure"
  const [viewMode, setViewMode] = useState("graph"); // "graph" | "table"
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [nodeCoords, setNodeCoords] = useState({});
  const [zoomState, setZoomState] = useState({
    zoom: 0.85,
    pan: { x: 0, y: 0 },
  });
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [isPanningCanvas, setIsPanningCanvas] = useState(false);

  // Filters (Separated cleanly by category and status)
  const [searchQuery, setSearchQuery] = useState("");
  const [appDomainFilter, setAppDomainFilter] = useState("");
  const [infraCategoryFilter, setInfraCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Local fallback modals
  const [localSelectedServer, setLocalSelectedServer] = useState(null);
  const [localSelectedApp, setLocalSelectedApp] = useState(null);

  // Application tree & summary data state
  const [appTree, setAppTree] = useState([]);
  const [appSummaryList, setAppSummaryList] = useState([]);

  const containerRef = useRef(null);
  const dragInfoRef = useRef(null);
  const fitViewRef = useRef(null);
  const zoomStateRef = useRef(zoomState);

  const storeServers = useAssetStore((state) => state.servers);
  const cmdbServers = useMemo(() => {
    if (Array.isArray(propServers) && propServers.length > 0)
      return propServers;
    return storeServers || [];
  }, [propServers, storeServers]);

  // Load Application Hierarchy Data
  useEffect(() => {
    let isMounted = true;
    Promise.all([
      fetchApplicationTree().catch(() => []),
      fetchApplicationSummary().catch(() => []),
    ])
      .then(([treeData, summaryData]) => {
        if (!isMounted) return;
        const validTree = Array.isArray(treeData) ? treeData : [];
        setAppTree(validTree);
        setAppSummaryList(Array.isArray(summaryData) ? summaryData : []);
        if (validTree.length === 0 && initialDomain !== "applications") {
          setDomain("infrastructure");
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialDomain]);

  // Server lookup map
  const cmdbServerMap = useMemo(() => {
    const map = new Map();
    cmdbServers.forEach((s) => {
      if (s.id) map.set(String(s.id).toLowerCase(), s);
      if (s.hostname) map.set(String(s.hostname).toLowerCase(), s);
      if (s.name) map.set(String(s.name).toLowerCase(), s);
    });
    return map;
  }, [cmdbServers]);

  // Application summary lookup map
  const appSummaryMap = useMemo(() => {
    const map = new Map();
    const sourceList =
      appSummaryList.length > 0 ? appSummaryList : propApplications || [];
    sourceList.forEach((a) => {
      if (!a) return;
      if (a.id) map.set(String(a.id), a);
      if (a.name) map.set(a.name.toLowerCase(), a);
    });
    return map;
  }, [appSummaryList, propApplications]);

  // ── 1. INFRASTRUCTURE GRAPH BUILDER ──────────────────────────────────────────
  const infraUnifiedNodes = useMemo(() => {
    const rawNodes = topology?.nodes || [];
    const processedNodes = [];
    const seenServerKeys = new Set();

    rawNodes.forEach((node) => {
      if (node.type === "server") {
        const rawId = node.id.replace(/^server:/, "");
        const matched =
          cmdbServerMap.get(rawId.toLowerCase()) ||
          cmdbServerMap.get((node.label || "").toLowerCase()) ||
          cmdbServerMap.get((node.metadata?.name || "").toLowerCase());

        const cat = getAssetCategory(
          matched ||
            node.metadata || { asset_category: node.metadata?.asset_category },
        );
        const isRouter = cat === "router" || cat === "network" || cat === "network_asset";

        const rawStatus = matched
          ? (isRouter ? getRouterStatus(matched) : getServerStatus(matched))
          : (node.metadata?.health_status || node.status || "active");

        const rawHealth =
          matched?.health_percent ?? node.metadata?.health_percent;

        const { healthScore, normalizedStatus } = computeHealthAndStatus(
          rawStatus,
          rawHealth,
        );

        const key = (matched?.hostname || node.label || rawId).toLowerCase();
        seenServerKeys.add(key);

        const ip =
          matched?.specs?.ip_address ||
          matched?.ip_address ||
          node.metadata?.specs?.ip_address ||
          node.metadata?.ip_address ||
          "—";

        processedNodes.push({
          id: node.id,
          rawId: matched?.id || rawId,
          label: matched?.hostname || node.label,
          name: matched?.name || node.metadata?.name || node.label,
          type: "server",
          asset_category: cat,
          status: normalizedStatus,
          health_score: healthScore,
          specs: matched?.specs || node.metadata?.specs || {},
          ip_address: ip,
          serverObject: matched || {
            id: rawId,
            hostname: node.label,
            name: node.metadata?.name || node.label,
            status: normalizedStatus,
            health_percent: healthScore,
            specs: node.metadata?.specs || {},
            ip_address: ip,
          },
        });
      }
    });

    // Ingest remaining servers from CMDB not in raw topology
    cmdbServers.forEach((s) => {
      const key = (s.hostname || s.name || s.id || "").toLowerCase();
      if (!seenServerKeys.has(key)) {
        seenServerKeys.add(key);
        const cat = getAssetCategory(s);
        const isRouter = cat === "router" || cat === "network" || cat === "network_asset";
        const rawStatus = isRouter ? getRouterStatus(s) : getServerStatus(s);
        const { healthScore, normalizedStatus } = computeHealthAndStatus(
          rawStatus,
          s.health_percent,
        );

        processedNodes.push({
          id: `server:${s.id}`,
          rawId: s.id,
          label: s.hostname || s.name || `server-${String(s.id).slice(0, 8)}`,
          name: s.name || s.hostname || "Server",
          type: "server",
          asset_category: cat,
          status: normalizedStatus,
          health_score: healthScore,
          specs: s.specs || {},
          ip_address: s.specs?.ip_address || s.ip_address || "—",
          serverObject: s,
        });
      }
    });

    return processedNodes;
  }, [topology, cmdbServers, cmdbServerMap]);

  const infraUnifiedEdges = useMemo(() => {
    const rawEdges = topology?.edges || [];
    const serverEdgesMap = {};

    rawEdges.forEach((edge) => {
      const isSrcService = edge.source.startsWith("service:");
      const isTgtService = edge.target.startsWith("service:");

      let srcServerId = edge.source;
      let tgtServerId = edge.target;

      if (isSrcService) {
        const svc = (topology?.nodes || []).find((n) => n.id === edge.source);
        if (svc?.metadata?.server_id) {
          srcServerId = `server:${svc.metadata.server_id}`;
        }
      }

      if (isTgtService) {
        const svc = (topology?.nodes || []).find((n) => n.id === edge.target);
        if (svc?.metadata?.server_id) {
          tgtServerId = `server:${svc.metadata.server_id}`;
        }
      }

      if (srcServerId && tgtServerId && srcServerId !== tgtServerId) {
        const edgeKey = `${srcServerId}->${tgtServerId}`;
        if (!serverEdgesMap[edgeKey]) {
          serverEdgesMap[edgeKey] = {
            id: edge.id || `edge-${edgeKey}`,
            source: srcServerId,
            target: tgtServerId,
            relation_type: edge.relation_type || "network_route",
            connections: [],
          };
        }
        serverEdgesMap[edgeKey].connections.push({
          id: edge.id,
          relation_type: edge.relation_type || "network_route",
        });
      }
    });

    return Object.values(serverEdgesMap);
  }, [topology]);

  // ── 2. BUSINESS APPLICATIONS GRAPH BUILDER (Real Data Driven) ───────────────
  const appUnifiedGraph = useMemo(() => {
    const nodes = [];
    const edges = [];
    const seenNodeIds = new Set();
    const seenEdgeIds = new Set();

    const rootApplications = appTree.length > 0 ? appTree : [];

    const serviceNodeMap = new Map();

    const registerServiceNode = (svc, parentId, rootId) => {
      const svcId = `service:${svc.id || svc.service_id}`;
      let node = serviceNodeMap.get(svcId);
      if (!node) {
        const rawStatus = isServiceRunning(svc) ? "active" : (svc.status || "inactive");
        const rawHealth =
          svc.health_score !== undefined ? svc.health_score : (rawStatus === "active" ? 100 : 0);
        const { healthScore, normalizedStatus } = computeHealthAndStatus(
          rawStatus,
          rawHealth,
        );

        const codeStr = svc.service_type
          ? `${svc.service_type}${svc.port ? `:${svc.port}` : ""}`
          : svc.port
            ? `Port ${svc.port}`
            : "Service";

        node = {
          id: svcId,
          rawId: svc.id || svc.service_id,
          label: svc.name || svc.service_name || "Service",
          name: svc.name || svc.service_name || "Service",
          code: codeStr,
          type: "app_service",
          tier: "Shared Service / DB",
          hostname: svc.hostname || "—",
          port: svc.port,
          status: normalizedStatus,
          health_score: healthScore,
          open_tickets_count: svc.open_tickets_count || 0,
          parent_ids: parentId ? [String(parentId)] : [],
          root_ids: rootId ? [String(rootId)] : [],
          serviceObject: svc,
        };
        serviceNodeMap.set(svcId, node);
        nodes.push(node);
        seenNodeIds.add(svcId);
      } else {
        if (parentId && !node.parent_ids.includes(String(parentId))) {
          node.parent_ids.push(String(parentId));
        }
        if (rootId && !node.root_ids.includes(String(rootId))) {
          node.root_ids.push(String(rootId));
        }
      }

      const svcEdgeId = `svc-link:${parentId}->${svc.id || svc.service_id}`;
      if (!seenEdgeIds.has(svcEdgeId)) {
        seenEdgeIds.add(svcEdgeId);
        edges.push({
          id: svcEdgeId,
          source: `app:${parentId}`,
          target: svcId,
          relation_type: "hosts_service",
          label: "hosts",
          connections: [{ id: svcEdgeId, relation_type: "hosts_service" }],
        });
      }
    };

    rootApplications.forEach((root) => {
      const rootSummary = appSummaryMap.get(String(root.id)) || {};
      const rootId = `app:${root.id}`;

      const rawStatus = getApplicationStatus(root);
      const rawHealth =
        root.health_score !== undefined && root.health_score !== null
          ? root.health_score
          : (rootSummary.health_score !== undefined && rootSummary.health_score !== null
              ? rootSummary.health_score
              : (rawStatus === "active" ? 100 : 0));
      const { healthScore, normalizedStatus } = computeHealthAndStatus(
        rawStatus,
        rawHealth,
      );

      if (!seenNodeIds.has(rootId)) {
        seenNodeIds.add(rootId);
        nodes.push({
          id: rootId,
          rawId: root.id,
          label: root.name,
          name: root.name,
          code: root.code,
          type: "app_root",
          tier: "Platform Root",
          owner_team: root.owner_team || rootSummary.owner_team || "—",
          health_score: healthScore,
          open_tickets_count:
            root.open_tickets_count ?? rootSummary.open_tickets_count ?? 0,
          status: normalizedStatus,
          services_count: (root.services || []).length,
          sub_apps_count: (root.sub_applications || []).length,
          appObject: root,
        });
      }

      (root.sub_applications || []).forEach((sub) => {
        const subSummary = appSummaryMap.get(String(sub.id)) || {};
        const subId = `app:${sub.id}`;

        const rawSubStatus = getApplicationStatus(sub);
        const rawSubHealth =
          sub.health_score !== undefined && sub.health_score !== null
            ? sub.health_score
            : (subSummary.health_score !== undefined && subSummary.health_score !== null
                ? subSummary.health_score
                : (rawSubStatus === "active" ? 100 : 0));
        const { healthScore: subHealth, normalizedStatus: subStatus } =
          computeHealthAndStatus(rawSubStatus, rawSubHealth);

        if (!seenNodeIds.has(subId)) {
          seenNodeIds.add(subId);
          nodes.push({
            id: subId,
            rawId: sub.id,
            label: sub.name,
            name: sub.name,
            code: sub.code,
            type: "sub_app",
            tier: "Microservice",
            parent_id: root.id,
            parent_name: root.name,
            owner_team:
              sub.owner_team || subSummary.owner_team || root.owner_team || "—",
            health_score: subHealth,
            open_tickets_count:
              sub.open_tickets_count ?? subSummary.open_tickets_count ?? 0,
            status: subStatus,
            services_count: (sub.services || []).length,
            appObject: sub,
          });
        }

        const edgeId = `hier:${root.id}->${sub.id}`;
        if (!seenEdgeIds.has(edgeId)) {
          seenEdgeIds.add(edgeId);
          edges.push({
            id: edgeId,
            source: rootId,
            target: subId,
            relation_type: "platform_hierarchy",
            label: "microservice",
            connections: [{ id: edgeId, relation_type: "platform_hierarchy" }],
          });
        }

        (sub.services || []).forEach((svc) =>
          registerServiceNode(svc, sub.id, root.id),
        );
      });

      (root.services || []).forEach((svc) =>
        registerServiceNode(svc, root.id, root.id),
      );
    });

    (topology?.edges || []).forEach((edge) => {
      const srcExists = seenNodeIds.has(edge.source);
      const tgtExists = seenNodeIds.has(edge.target);
      if (srcExists && tgtExists) {
        const depEdgeId = `dep:${edge.source}->${edge.target}`;
        if (!seenEdgeIds.has(depEdgeId)) {
          seenEdgeIds.add(depEdgeId);
          edges.push({
            id: depEdgeId,
            source: edge.source,
            target: edge.target,
            relation_type: "service_dependency",
            label: edge.relation_type || "depends_on",
            connections: [
              { id: depEdgeId, relation_type: "service_dependency" },
            ],
          });
        }
      }
    });

    return { nodes, edges };
  }, [appTree, appSummaryMap, topology]);

  // Dynamic available categories from real loaded infrastructure nodes
  const availableAssetCategories = useMemo(() => {
    const set = new Set();
    infraUnifiedNodes.forEach((n) => {
      if (n.asset_category) set.add(n.asset_category);
    });
    return Array.from(set).map((cat) => ({
      value: cat,
      label: formatCategoryLabel(cat),
    }));
  }, [infraUnifiedNodes]);

  const activeNodes =
    domain === "applications" ? appUnifiedGraph.nodes : infraUnifiedNodes;
  const activeEdges =
    domain === "applications" ? appUnifiedGraph.edges : infraUnifiedEdges;

  // Filtered nodes (Zero duplicate options, real database categories)
  const filteredNodes = useMemo(() => {
    return activeNodes.filter((node) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (node.label && node.label.toLowerCase().includes(q)) ||
        (node.name && node.name.toLowerCase().includes(q)) ||
        (node.code && node.code.toLowerCase().includes(q)) ||
        (node.owner_team && node.owner_team.toLowerCase().includes(q)) ||
        (node.ip_address && node.ip_address.toLowerCase().includes(q));

      if (!matchesSearch) return false;

      if (statusFilter && statusFilter !== "all") {
        if (statusFilter === "healthy" && node.health_score < 90) return false;
        if (
          statusFilter === "degraded" &&
          (node.health_score < 50 || node.health_score >= 90)
        )
          return false;
        if (
          statusFilter === "critical" &&
          (node.health_score >= 50 || node.status === "inactive")
        )
          return false;
        if (statusFilter === "inactive" && node.status !== "inactive")
          return false;
        if (statusFilter === "active" && node.status === "inactive")
          return false;
        if (statusFilter === "issues" && node.health_score >= 90) return false;
      }

      if (domain === "applications") {
        if (appDomainFilter && appDomainFilter !== "all") {
          const selectedStr = String(appDomainFilter);
          const isSelectedRoot = node.id === `app:${selectedStr}`;
          const isChildOfRoot =
            String(node.parent_id) === selectedStr ||
            (Array.isArray(node.parent_ids) &&
              node.parent_ids.includes(selectedStr));
          const isRootService =
            Array.isArray(node.root_ids) && node.root_ids.includes(selectedStr);
          if (!isSelectedRoot && !isChildOfRoot && !isRootService) return false;
        }
      } else {
        if (
          infraCategoryFilter &&
          infraCategoryFilter !== "all" &&
          node.asset_category !== infraCategoryFilter
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    activeNodes,
    searchQuery,
    domain,
    appDomainFilter,
    infraCategoryFilter,
    statusFilter,
  ]);

  // Dynamically derived categories present in current view for the bottom legend
  const presentCategories = useMemo(() => {
    if (domain === "applications") {
      const tiers = new Set();
      filteredNodes.forEach((n) => {
        if (n.type && APPLICATION_TIER_STYLES[n.type]) tiers.add(n.type);
      });
      return Array.from(tiers).map((t) => APPLICATION_TIER_STYLES[t]);
    } else {
      const cats = new Set();
      filteredNodes.forEach((n) => {
        if (n.asset_category) cats.add(n.asset_category);
      });
      return Array.from(cats).map((c) => getCategoryStyle(c));
    }
  }, [filteredNodes, domain]);

  const nodesMap = useMemo(() => {
    return Object.fromEntries(activeNodes.map((n) => [n.id, n]));
  }, [activeNodes]);

  const filteredEdges = useMemo(() => {
    const activeIds = new Set(filteredNodes.map((n) => n.id));
    return activeEdges.filter(
      (e) => activeIds.has(e.source) && activeIds.has(e.target),
    );
  }, [activeEdges, filteredNodes]);

  // Content-Responsive Node Dimensions (Expands dynamically to fit full content)
  const nodeDimensions = useMemo(() => {
    const dims = {};
    activeNodes.forEach((node) => {
      const nameStr = String(node.name || node.label || "");
      let subStr = "";
      if (domain === "applications") {
        if (node.code) {
          subStr = `${node.code}${node.owner_team && node.owner_team !== "—" ? ` · ${node.owner_team}` : ""}`;
        } else if (node.hostname && node.hostname !== "—") {
          subStr = `${node.hostname}${node.port ? `:${node.port}` : ""}`;
        } else if (node.port) {
          subStr = `Port ${node.port}`;
        } else if (node.owner_team && node.owner_team !== "—") {
          subStr = `Team: ${node.owner_team}`;
        } else if (node.tier) {
          subStr = node.tier;
        }
      } else {
        subStr =
          node.ip_address && node.ip_address !== "—"
            ? `IP: ${node.ip_address}`
            : node.specs?.os || "Server";
      }

      // Approximate text width: title ~7.5px/char, subtitle ~6.2px/char
      const nameWidth = nameStr.length * 7.5;
      const subWidth = subStr.length * 6.2;
      const headerWidth = 160; // Badge (70px) + Gap + Status Pill (64px)
      const contentWidth = Math.max(nameWidth, subWidth, headerWidth);

      // Adaptive width with padding, clamped between 210px and 340px
      const width = Math.min(340, Math.max(210, Math.round(contentWidth + 36)));
      const height = 76;

      dims[node.id] = { width, height, subStr };
    });
    return dims;
  }, [activeNodes, domain]);

  // Compute Layout Positions
  useEffect(() => {
    if (activeNodes.length === 0) return;
    const initialCoords = computeInitialPositions(
      activeNodes,
      activeEdges,
      1000,
      560,
      domain,
    );
    setNodeCoords(initialCoords);
  }, [activeNodes, activeEdges, domain]);

  // Dynamic Edge boundary offset calculation based on responsive node dimensions
  const getNodeBorderOffset = (nodeId, dx, dy, len) => {
    if (!len || len === 0) return 50;
    const dims = nodeDimensions[nodeId] || { width: 210, height: 76 };
    const boxWidthHalf = dims.width / 2 + 8;
    const boxHeightHalf = dims.height / 2 + 8;
    const absDx = Math.abs(dx / len);
    const absDy = Math.abs(dy / len);

    return Math.min(
      absDx > 0.001 ? boxWidthHalf / absDx : Infinity,
      absDy > 0.001 ? boxHeightHalf / absDy : Infinity,
    );
  };

  const getEdgePath = (sourceId, targetId) => {
    const p1 = nodeCoords[sourceId];
    const p2 = nodeCoords[targetId];
    if (!p1 || !p2) return "";

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;

    const offset1 = getNodeBorderOffset(sourceId, dx, dy, len);
    const offset2 = getNodeBorderOffset(targetId, -dx, -dy, len);

    const startX = p1.x + (dx / len) * offset1;
    const startY = p1.y + (dy / len) * offset1;
    const endX = p2.x - (dx / len) * offset2;
    const endY = p2.y - (dy / len) * offset2;

    const hasReverse = filteredEdges.some(
      (e) => e.source === targetId && e.target === sourceId,
    );

    if (hasReverse) {
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      const nx = -dy / len;
      const ny = dx / len;
      const offset = 26;
      const cx = midX + nx * offset;
      const cy = midY + ny * offset;
      return `M ${startX} ${startY} Q ${cx} ${cy} ${endX} ${endY}`;
    }

    return `M ${startX} ${startY} L ${endX} ${endY}`;
  };

  // Fit View (Auto Zoom Reduction to fit ALL dynamic nodes comfortably)
  const fitView = useCallback(() => {
    if (!containerRef.current) return;
    const containerW = containerRef.current.clientWidth || 800;
    const containerH = containerRef.current.clientHeight || 560;

    const nodeIds = Object.keys(nodeCoords);
    if (nodeIds.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodeIds.forEach((id) => {
      const coord = nodeCoords[id];
      if (!coord) return;
      const dims = nodeDimensions[id] || { width: 210, height: 76 };
      const halfW = dims.width / 2 + 16;
      const halfH = dims.height / 2 + 16;
      const { x, y } = coord;
      if (x - halfW < minX) minX = x - halfW;
      if (x + halfW > maxX) maxX = x + halfW;
      if (y - halfH < minY) minY = y - halfH;
      if (y + halfH > maxY) maxY = y + halfH;
    });

    if (minX === Infinity || maxX === -Infinity) return;

    const graphW = Math.max(maxX - minX, 200);
    const graphH = Math.max(maxY - minY, 200);
    const graphCenterX = (minX + maxX) / 2;
    const graphCenterY = (minY + maxY) / 2;

    const padding = containerW < 600 ? 40 : 70;
    const availW = Math.max(containerW - padding * 2, 100);
    const availH = Math.max(containerH - padding * 2, 100);

    const scaleX = availW / graphW;
    const scaleY = availH / graphH;
    let fittedScale = Math.min(scaleX, scaleY) * 0.86;
    fittedScale = Math.min(Math.max(fittedScale, 0.2), 0.92);

    const targetPanX = containerW / 2 - graphCenterX * fittedScale;
    const targetPanY = containerH / 2 - graphCenterY * fittedScale;

    setZoomState({
      zoom: fittedScale,
      pan: { x: targetPanX, y: targetPanY },
    });
  }, [nodeCoords, nodeDimensions]);

  useEffect(() => {
    fitViewRef.current = fitView;
  }, [fitView]);

  useEffect(() => {
    if (Object.keys(nodeCoords).length > 0) {
      const timer = setTimeout(() => {
        fitView();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [nodeCoords, fitView]);

  useLayoutEffect(() => {
    if (viewMode !== "graph" || !containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          requestAnimationFrame(() => {
            if (fitViewRef.current) fitViewRef.current();
          });
        }
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [viewMode, domain]);

  // Pointer Interaction Handlers
  const handleNodePointerDown = (e, nodeId) => {
    e.stopPropagation();
    if (e.button !== undefined && e.button !== 0) return;

    const currentCoords = nodeCoords[nodeId] || { x: 0, y: 0 };
    dragInfoRef.current = {
      type: "node",
      nodeId,
      startX: e.clientX,
      startY: e.clientY,
      initialNodeX: currentCoords.x,
      initialNodeY: currentCoords.y,
    };
    setIsDraggingNode(true);
  };

  const handleCanvasPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;

    dragInfoRef.current = {
      type: "pan",
      startX: e.clientX,
      startY: e.clientY,
      initialPanX: zoomStateRef.current.pan.x,
      initialPanY: zoomStateRef.current.pan.y,
    };
    setIsPanningCanvas(true);
  };

  useEffect(() => {
    const handleGlobalPointerMove = (e) => {
      if (!dragInfoRef.current) return;

      if (dragInfoRef.current.type === "node") {
        const currentZoom = zoomStateRef.current.zoom || 1;
        const dx = (e.clientX - dragInfoRef.current.startX) / currentZoom;
        const dy = (e.clientY - dragInfoRef.current.startY) / currentZoom;
        const targetId = dragInfoRef.current.nodeId;

        setNodeCoords((prev) => ({
          ...prev,
          [targetId]: {
            x: dragInfoRef.current.initialNodeX + dx,
            y: dragInfoRef.current.initialNodeY + dy,
          },
        }));
      } else if (dragInfoRef.current.type === "pan") {
        const dx = e.clientX - dragInfoRef.current.startX;
        const dy = e.clientY - dragInfoRef.current.startY;

        setZoomState((prev) => ({
          ...prev,
          pan: {
            x: dragInfoRef.current.initialPanX + dx,
            y: dragInfoRef.current.initialPanY + dy,
          },
        }));
      }
    };

    const handleGlobalPointerUp = () => {
      if (dragInfoRef.current) {
        dragInfoRef.current = null;
        setIsDraggingNode(false);
        setIsPanningCanvas(false);
      }
    };

    window.addEventListener("pointermove", handleGlobalPointerMove);
    window.addEventListener("pointerup", handleGlobalPointerUp);
    window.addEventListener("pointercancel", handleGlobalPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);
      window.removeEventListener("pointercancel", handleGlobalPointerUp);
    };
  }, []);

  const handleZoomIn = () => {
    if (!containerRef.current) return;
    const containerW = containerRef.current.clientWidth || 800;
    const containerH = containerRef.current.clientHeight || 480;
    const centerX = containerW / 2;
    const centerY = containerH / 2;

    setZoomState((prev) => {
      const newScale = Math.min(prev.zoom * 1.25, 4.0);
      const newPanX = centerX - (centerX - prev.pan.x) * (newScale / prev.zoom);
      const newPanY = centerY - (centerY - prev.pan.y) * (newScale / prev.zoom);
      return { zoom: newScale, pan: { x: newPanX, y: newPanY } };
    });
  };

  const handleZoomOut = () => {
    if (!containerRef.current) return;
    const containerW = containerRef.current.clientWidth || 800;
    const containerH = containerRef.current.clientHeight || 480;
    const centerX = containerW / 2;
    const centerY = containerH / 2;

    setZoomState((prev) => {
      const newScale = Math.max(prev.zoom / 1.25, 0.15);
      const newPanX = centerX - (centerX - prev.pan.x) * (newScale / prev.zoom);
      const newPanY = centerY - (centerY - prev.pan.y) * (newScale / prev.zoom);
      return { zoom: newScale, pan: { x: newPanX, y: newPanY } };
    });
  };

  const handleOpenNodeDetails = (node) => {
    if (!node) return;
    if (node.appObject) {
      if (onSelectApplication) {
        onSelectApplication(node.appObject);
      } else {
        setLocalSelectedApp(node.appObject);
      }
    } else if (node.serverObject) {
      if (onSelectServer) {
        onSelectServer(node.serverObject);
      } else {
        setLocalSelectedServer(node.serverObject);
      }
    }
  };



  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        width: "100%",
      }}
    >
      {/* TOPOLOGY HEADER & DOMAIN SELECTOR */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          background: "#FFFFFF",
          padding: "12px 18px",
          borderRadius: 10,
          border: "1px solid #E2E8F0",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0F172A",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Icon
                name={domain === "applications" ? "layers" : "network"}
                size={18}
                style={{
                  color: domain === "applications" ? "#7C3AED" : "#2563EB",
                }}
              />
              {domain === "applications"
                ? "Business Application & Microservice Service Map"
                : "Network & Asset Infrastructure Topology"}
            </h3>
            <p style={{ fontSize: 11, color: "#64748B", margin: "2px 0 0" }}>
              {domain === "applications"
                ? `${filteredNodes.length} Application Nodes · ${filteredEdges.length} Request & Dependency Flows`
                : `${filteredNodes.length} Assets · ${filteredEdges.length} Network Routes (Central CMDB Sync)`}
            </p>
          </div>

          {/* Domain Mode Switcher */}
          <div
            style={{
              display: "flex",
              gap: 4,
              background: "#F1F5F9",
              padding: "3px",
              borderRadius: 8,
              border: "1px solid #E2E8F0",
            }}
          >
            <button
              type="button"
              onClick={() => {
                setDomain("applications");
                setViewMode("graph");
                setAppDomainFilter("");
                setInfraCategoryFilter("");
                setStatusFilter("");
                setSearchQuery("");
              }}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: 11.5,
                fontWeight: domain === "applications" ? 700 : 500,
                background:
                  domain === "applications" ? "#FFFFFF" : "transparent",
                color: domain === "applications" ? "#1D4ED8" : "#64748B",
                border:
                  domain === "applications" ? "1px solid #CBD5E1" : "none",
                boxShadow:
                  domain === "applications"
                    ? "0 1px 2px rgba(0,0,0,0.06)"
                    : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.15s ease",
              }}
            >
              <Icon
                name="layers"
                size={13}
                color={domain === "applications" ? "#1D4ED8" : "#64748B"}
              />
              Business Applications Map
            </button>

            <button
              type="button"
              onClick={() => {
                setDomain("infrastructure");
                setViewMode("graph");
                setAppDomainFilter("");
                setInfraCategoryFilter("");
                setStatusFilter("");
                setSearchQuery("");
              }}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: 11.5,
                fontWeight: domain === "infrastructure" ? 700 : 500,
                background:
                  domain === "infrastructure" ? "#FFFFFF" : "transparent",
                color: domain === "infrastructure" ? "#1D4ED8" : "#64748B",
                border:
                  domain === "infrastructure" ? "1px solid #CBD5E1" : "none",
                boxShadow:
                  domain === "infrastructure"
                    ? "0 1px 2px rgba(0,0,0,0.06)"
                    : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.15s ease",
              }}
            >
              <Icon
                name="network"
                size={13}
                color={domain === "infrastructure" ? "#1D4ED8" : "#64748B"}
              />
              Infrastructure Network
            </button>
          </div>
        </div>

        {/* View Mode Switcher */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              display: "flex",
              border: "1px solid #CBD5E1",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <button
              type="button"
              onClick={() => setViewMode("graph")}
              style={{
                padding: "5px 10px",
                fontSize: 11.5,
                fontWeight: viewMode === "graph" ? 700 : 500,
                background: viewMode === "graph" ? "#EFF6FF" : "#FFFFFF",
                color: viewMode === "graph" ? "#1D4ED8" : "#64748B",
                border: "none",
                borderRight: "1px solid #CBD5E1",
                cursor: "pointer",
              }}
            >
              Graph View
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              style={{
                padding: "5px 10px",
                fontSize: 11.5,
                fontWeight: viewMode === "table" ? 700 : 500,
                background: viewMode === "table" ? "#EFF6FF" : "#FFFFFF",
                color: viewMode === "table" ? "#1D4ED8" : "#64748B",
                border: "none",
                cursor: "pointer",
              }}
            >
              Table View
            </button>
          </div>
        </div>
      </div>

      {/* FILTER CONTROLS (Zero Duplication & Fully Dynamic) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            flex: 1,
            minWidth: 260,
          }}
        >
          <SearchInput
            placeholder={
              domain === "applications"
                ? "Search applications, microservices, or services…"
                : "Search servers by name, hostname, or IP…"
            }
            value={searchQuery}
            onChange={setSearchQuery}
            style={{ flex: 1, minWidth: 220 }}
          />

          {domain === "applications" ? (
            <>
              <FilterSelect
                value={appDomainFilter}
                onChange={setAppDomainFilter}
                placeholder="All Platform Domains"
                options={appTree.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.code || "Root"})`,
                }))}
              />

              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                options={[
                  { value: "healthy", label: "Healthy (≥90%)" },
                  { value: "degraded", label: "Degraded (50-89%)" },
                  { value: "critical", label: "Critical (<50%)" },
                  { value: "inactive", label: "Inactive" },
                ]}
              />
            </>
          ) : (
            <>
              <FilterSelect
                value={infraCategoryFilter}
                onChange={setInfraCategoryFilter}
                placeholder="All Asset Categories"
                options={availableAssetCategories}
              />

              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                options={[
                  { value: "active", label: "Active Status" },
                  { value: "issues", label: "Issues / Degraded" },
                  { value: "inactive", label: "Inactive / Offline" },
                ]}
              />
            </>
          )}
        </div>

        {viewMode === "graph" && (
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={handleZoomIn}
              title="Zoom In"
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                border: "1px solid #CBD5E1",
                background: "#FFFFFF",
                color: "#475569",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              +
            </button>
            <button
              type="button"
              onClick={handleZoomOut}
              title="Zoom Out"
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                border: "1px solid #CBD5E1",
                background: "#FFFFFF",
                color: "#475569",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              −
            </button>
            <button
              type="button"
              onClick={fitView}
              title="Fit to View"
              style={{
                padding: "0 10px",
                height: 32,
                borderRadius: 6,
                border: "1px solid #CBD5E1",
                background: "#FFFFFF",
                color: "#475569",
                cursor: "pointer",
                fontSize: 11.5,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              Fit View
            </button>
          </div>
        )}
      </div>

      {/* GRAPH CANVAS OR TABLE CONTAINER */}
      {viewMode === "graph" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 0,
            width: "100%",
          }}
        >
          <div
            ref={containerRef}
            onPointerDown={handleCanvasPointerDown}
            style={{
              position: "relative",
              width: "100%",
              height: 560,
              background: "#FAFAFA",
              border: "1px solid #E2E8F0",
              borderRadius: "12px 12px 0 0",
              overflow: "hidden",
              cursor: isDraggingNode
                ? "grabbing"
                : isPanningCanvas
                  ? "grabbing"
                  : "grab",
              userSelect: "none",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            {/* Subtle Grid Pattern */}
            <svg
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <defs>
                <pattern
                  id="top-grid-pattern"
                  width="32"
                  height="32"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="1" cy="1" r="1" fill="#E2E8F0" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#top-grid-pattern)" />
            </svg>

            {/* Empty State Overlay if domain has no matching nodes */}
            {filteredNodes.length === 0 ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 30,
                  textAlign: "center",
                  zIndex: 2,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: domain === "applications" ? "#F5F3FF" : "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 14,
                  }}
                >
                  <Icon
                    name={domain === "applications" ? "layers" : "network"}
                    size={28}
                    color={domain === "applications" ? "#7C3AED" : "#2563EB"}
                  />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1E293B", margin: "0 0 6px" }}>
                  {domain === "applications"
                    ? (searchQuery || appDomainFilter || statusFilter
                        ? "No matching application nodes"
                        : "No Business Applications Configured")
                    : (searchQuery || infraCategoryFilter || statusFilter
                        ? "No matching infrastructure nodes"
                        : "No Network Infrastructure Topology")}
                </h3>
                <p style={{ fontSize: 13, color: "#64748B", maxWidth: 460, margin: "0 0 16px", lineHeight: 1.5 }}>
                  {domain === "applications"
                    ? (searchQuery || appDomainFilter || statusFilter
                        ? "Try adjusting or clearing your search keywords and status filters."
                        : "No application topology records found. You can view the live Infrastructure Network topology or map applications in Application Management.")
                    : (searchQuery || infraCategoryFilter || statusFilter
                        ? "Try adjusting or clearing your search keywords and status filters."
                        : "Connect servers or run subnet discovery to populate network topology nodes.")}
                </p>
                {domain === "applications" && appTree.length === 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setDomain("infrastructure");
                      setViewMode("graph");
                      setSearchQuery("");
                      setStatusFilter("");
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#2563EB",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 7,
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Icon name="network" size={14} color="#FFFFFF" />
                    Switch to Infrastructure Network Topology
                  </button>
                )}
              </div>
            ) : null}

            {/* Topology SVG */}
            <svg
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                display: filteredNodes.length === 0 ? "none" : "block",
              }}
            >
              <defs>
                {/* Arrowhead Markers */}
                <marker
                  id="arrow-default"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1 L 10 5 L 0 9 z" fill="#94A3B8" />
                </marker>
                <marker
                  id="arrow-hover"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1 L 10 5 L 0 9 z" fill="#1D4ED8" />
                </marker>
                <marker
                  id="arrow-platform"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1 L 10 5 L 0 9 z" fill="#7C3AED" />
                </marker>
                <marker
                  id="arrow-service"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1 L 10 5 L 0 9 z" fill="#0891B2" />
                </marker>

                {/* Glow Filters */}
                <filter
                  id="glow-hover"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                >
                  <feDropShadow
                    dx="0"
                    dy="4"
                    stdDeviation="6"
                    floodColor="#1D4ED8"
                    floodOpacity="0.25"
                  />
                </filter>
                <filter
                  id="glow-critical"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                >
                  <feDropShadow
                    dx="0"
                    dy="4"
                    stdDeviation="6"
                    floodColor="#DC2626"
                    floodOpacity="0.35"
                  />
                </filter>
              </defs>

              {/* Pan & Zoom Group */}
              <g
                transform={`translate(${zoomState.pan.x}, ${zoomState.pan.y}) scale(${zoomState.zoom})`}
              >
                {/* Edges & Particle Flows */}
                {filteredEdges.map((edge) => {
                  const path = getEdgePath(edge.source, edge.target);
                  if (!path) return null;

                  const isHovered =
                    hoveredEdge?.id === edge.id ||
                    (hoveredNode &&
                      (edge.source === hoveredNode ||
                        edge.target === hoveredNode));

                  const isHierarchy =
                    edge.relation_type === "platform_hierarchy";
                  const isServiceLink = edge.relation_type === "hosts_service";

                  const strokeColor = isHovered
                    ? "#1D4ED8"
                    : isHierarchy
                      ? "#818CF8"
                      : isServiceLink
                        ? "#38BDF8"
                        : "#94A3B8";

                  const strokeWidth = isHovered ? 2.5 : isHierarchy ? 2.0 : 1.5;
                  const markerEnd = isHovered
                    ? "url(#arrow-hover)"
                    : isHierarchy
                      ? "url(#arrow-platform)"
                      : isServiceLink
                        ? "url(#arrow-service)"
                        : "url(#arrow-default)";

                  return (
                    <g
                      key={edge.id}
                      onMouseEnter={() => setHoveredEdge(edge)}
                      onMouseLeave={() => setHoveredEdge(null)}
                      style={{ cursor: "pointer" }}
                    >
                      <path
                        d={path}
                        stroke="transparent"
                        strokeWidth="18"
                        fill="none"
                      />
                      <path
                        d={path}
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={isHierarchy ? "6,4" : "none"}
                        fill="none"
                        markerEnd={markerEnd}
                        style={{ transition: "stroke 0.2s, stroke-width 0.2s" }}
                      />
                      {isHovered && (
                        <circle r="3.5" fill="#1D4ED8">
                          <animateMotion
                            dur="2.2s"
                            repeatCount="indefinite"
                            path={path}
                          />
                        </circle>
                      )}
                    </g>
                  );
                })}

                {/* Nodes (Zero Text Overflow, Strict Real Asset Category & Tier Mapping) */}
                {filteredNodes.map((node) => {
                  const coords = nodeCoords[node.id];
                  if (!coords) return null;

                  const isHovered = hoveredNode === node.id;
                  const isRelated =
                    hoveredNode &&
                    (filteredEdges.some(
                      (e) => e.source === hoveredNode && e.target === node.id,
                    ) ||
                      filteredEdges.some(
                        (e) => e.target === hoveredNode && e.source === node.id,
                      ));

                  const isDimmed = hoveredNode && !isHovered && !isRelated;

                  const styleCfg =
                    domain === "applications"
                      ? APPLICATION_TIER_STYLES[node.type] ||
                        APPLICATION_TIER_STYLES.app_root
                      : getCategoryStyle(node.asset_category);

                  const statusKey = (node.status || "inactive").toLowerCase();
                  const isActive =
                    statusKey === "active" ||
                    statusKey === "healthy" ||
                    statusKey === "running";
                  const displayStatus = isActive ? "active" : "inactive";
                  const statusStyle = getStatusStyle(displayStatus);

                  const statusColor = statusStyle.color;
                  const statusBg = statusStyle.bg;
                  const statusBorder = statusStyle.border || statusStyle.bg;

                  let cardStroke = isHovered
                    ? "#1D4ED8"
                    : isActive
                      ? "#E2E8F0"
                      : "#FECACA";

                  let cardStrokeWidth = isHovered ? 2.5 : 1.5;
                  let cardFilter = isHovered ? "url(#glow-hover)" : "none";

                  const dims = nodeDimensions[node.id] || {
                    width: 210,
                    height: 76,
                    subStr: "",
                  };
                  const halfW = dims.width / 2;
                  const halfH = dims.height / 2;
                  const pad = 12;
                  const cleanNodeId = String(node.id).replace(/[^a-zA-Z0-9_-]/g, "_");
                  const fullName = String(node.name || node.label || "Node");
                  const subtitle = dims.subStr;

                  return (
                    <g
                      key={node.id}
                      transform={`translate(${coords.x}, ${coords.y})`}
                      onPointerDown={(e) => handleNodePointerDown(e, node.id)}
                      onMouseEnter={() => setHoveredNode(node.id)}
                      onMouseLeave={() => setHoveredNode(null)}
                      onClick={() => handleOpenNodeDetails(node)}
                      style={{
                        cursor: "pointer",
                        opacity: isDimmed ? 0.35 : 1,
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      <defs>
                        <clipPath id={`clip-${cleanNodeId}`}>
                          <rect
                            x={-halfW + 4}
                            y={-halfH + 4}
                            width={dims.width - 8}
                            height={dims.height - 8}
                            rx="8"
                          />
                        </clipPath>
                      </defs>

                      {/* Responsive Node Body Card */}
                      <rect
                        x={-halfW}
                        y={-halfH}
                        width={dims.width}
                        height={dims.height}
                        rx="10"
                        fill="#FFFFFF"
                        stroke={cardStroke}
                        strokeWidth={cardStrokeWidth}
                        filter={cardFilter}
                        style={{ transition: "stroke 0.2s, filter 0.2s" }}
                      />

                      {/* Top Accent Line */}
                      <path
                        d={`M ${-halfW + 10} ${-halfH} L ${halfW - 10} ${-halfH}`}
                        stroke={styleCfg.color}
                        strokeWidth="3.5"
                        strokeLinecap="round"
                      />

                      {/* Clipped Inner Container (Strict zero-spill guarantee) */}
                      <g clipPath={`url(#clip-${cleanNodeId})`}>
                        {/* Left Tier / Asset Category Badge (Width 70, Height 14) */}
                        <g transform={`translate(${-halfW + pad}, ${-halfH + 10})`}>
                          <rect
                            x="0"
                            y="0"
                            width="70"
                            height="14"
                            rx="4"
                            fill={styleCfg.bg}
                            stroke={styleCfg.border}
                            strokeWidth="0.6"
                          />
                          <text
                            x="35"
                            y="10"
                            fontSize="7.5"
                            fontWeight="700"
                            fill={styleCfg.color}
                            textAnchor="middle"
                          >
                            {truncate(styleCfg.badge, 10)}
                          </text>
                        </g>

                        {/* Right Status Pill (Width 64, Height 14) */}
                        <g transform={`translate(${halfW - pad - 64}, ${-halfH + 10})`}>
                          <rect
                            x="0"
                            y="0"
                            width="64"
                            height="14"
                            rx="4"
                            fill={statusBg}
                            stroke={statusBorder}
                            strokeWidth="0.6"
                          />
                          <text
                            x="32"
                            y="10"
                            fontSize="7.5"
                            fontWeight="700"
                            fill={statusColor}
                            textAnchor="middle"
                          >
                            {displayStatus.toUpperCase()}
                          </text>
                        </g>

                        {/* Node Name (Full title rendered within responsive width) */}
                        <text
                          x={-halfW + pad}
                          y="6"
                          fontSize="11.5"
                          fontWeight="700"
                          fill="#0F172A"
                          textAnchor="start"
                        >
                          <title>{fullName}</title>
                          {fullName}
                        </text>

                        {/* Subtitle Line (Full subtitle rendered within responsive width) */}
                        <text
                          x={-halfW + pad}
                          y="22"
                          fontSize="9.5"
                          fontWeight="500"
                          fill="#64748B"
                          textAnchor="start"
                        >
                          <title>{subtitle}</title>
                          {subtitle}
                        </text>
                      </g>
                    </g>
                  );
                })}
              </g>
            </svg>

            {/* Bottom-Left Dynamic Domain & Category Legend */}
            {presentCategories.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  left: 10,
                  background: "rgba(255,255,255,0.94)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  padding: "6px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  fontSize: 10.5,
                  color: "#64748B",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                {presentCategories.map((item) => (
                  <span
                    key={item.label}
                    style={{ display: "flex", alignItems: "center", gap: 5 }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: item.color,
                      }}
                    />
                    {item.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* RESTORED RICH BOTTOM INSPECTION BAR */}
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              padding: "10px 16px",
              minHeight: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
            }}
          >
            {hoveredEdge ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                    }}
                  >
                    Active Route / Flow:
                  </span>
                  <strong style={{ color: "#0F172A" }}>
                    {nodesMap[hoveredEdge.source]?.name || hoveredEdge.source}
                  </strong>
                  <span style={{ color: "#2563EB", fontWeight: 700 }}>──►</span>
                  <strong style={{ color: "#0F172A" }}>
                    {nodesMap[hoveredEdge.target]?.name || hoveredEdge.target}
                  </strong>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 12,
                      background: "#EFF6FF",
                      color: "#1D4ED8",
                    }}
                  >
                    {hoveredEdge.relation_type || "connected"}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  Direction: Source Request Flow
                </div>
              </div>
            ) : hoveredNode && nodesMap[hoveredNode] ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <strong style={{ fontSize: 13, color: "#0F172A" }}>
                    {nodesMap[hoveredNode].name}
                  </strong>
                  <StatusBadge status={nodesMap[hoveredNode].status} />
                  <span style={{ fontSize: 11, color: "#64748B" }}>
                    {domain === "applications"
                      ? `Team: ${nodesMap[hoveredNode].owner_team || "—"} | Code: ${nodesMap[hoveredNode].code || "—"}`
                      : `Category: ${formatCategoryLabel(nodesMap[hoveredNode].asset_category)} | IP: ${nodesMap[hoveredNode].ip_address}`}
                  </span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span style={{ fontSize: 11, color: "#475569" }}>
                      Health:
                    </span>
                    <strong
                      style={{
                        fontSize: 12,
                        color:
                          nodesMap[hoveredNode].status === "inactive"
                            ? "#64748B"
                            : nodesMap[hoveredNode].health_score >= 90
                              ? "#16A34A"
                              : nodesMap[hoveredNode].health_score >= 50
                                ? "#D97706"
                                : "#DC2626",
                      }}
                    >
                      {nodesMap[hoveredNode].status === "inactive"
                        ? "0% (Inactive)"
                        : `${nodesMap[hoveredNode].health_score.toFixed(0)}%`}
                    </strong>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleOpenNodeDetails(nodesMap[hoveredNode])}
                  style={{
                    padding: "6px 12px",
                    background: "#2563EB",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 6,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {domain === "applications"
                    ? "View Application Details →"
                    : "View Server Details →"}
                </button>
              </div>
            ) : (
              <div
                style={{
                  fontSize: 11,
                  color: "#64748B",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                💡 Click on any node or connection link to inspect live
                telemetry, health scores, and dependencies. Drag nodes to
                customize canvas layout.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* TABLE VIEW (Tailored per Domain) */
        <DataTable
          columns={
            domain === "applications"
              ? [
                  {
                    header: "Application / Service",
                    key: "name",
                    render: (_, row) => (
                      <div>
                        <div style={{ fontWeight: 600, color: "#0F172A" }}>
                          {row.name}
                        </div>
                        {row.code && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#64748B",
                              fontFamily: "var(--font-mono, monospace)",
                            }}
                          >
                            {row.code}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    header: "Tier",
                    key: "type",
                    render: (_, row) => {
                      const cfg =
                        APPLICATION_TIER_STYLES[row.type] ||
                        APPLICATION_TIER_STYLES.app_root;
                      return (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: cfg.bg,
                            color: cfg.color,
                          }}
                        >
                          {cfg.label}
                        </span>
                      );
                    },
                  },
                  {
                    header: "Owner Team",
                    key: "owner_team",
                    render: (val, row) => (
                      <span style={{ color: "#475569" }}>
                        {val || row.hostname || "—"}
                      </span>
                    ),
                  },
                  {
                    header: "Status & Health",
                    key: "health_score",
                    render: (val, row) => {
                      const numVal = Number(val ?? 0);
                      const isValid = val !== null && val !== undefined;
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <StatusBadge status={row.status || "inactive"} />
                          <strong style={{ color: "#1E293B", fontSize: 12 }}>
                            {isValid ? `${numVal.toFixed(0)}%` : "—"}
                          </strong>
                        </div>
                      );
                    },
                  },
                  {
                    header: "Action",
                    key: "action",
                    align: "right",
                    render: (_, row) => (
                      <button
                        type="button"
                        onClick={() => handleOpenNodeDetails(row)}
                        style={{
                          padding: "4px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          background: "#EFF6FF",
                          color: "#1D4ED8",
                          border: "1px solid #BFDBFE",
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        Details →
                      </button>
                    ),
                  },
                ]
              : [
                  {
                    header: "Source Host / Device",
                    key: "source",
                    render: (_, edge) => {
                      const sourceNode = nodesMap[edge.source];
                      return (
                        <span style={{ fontWeight: 600, color: "#0F172A" }}>
                          {sourceNode?.name || sourceNode?.label || edge.source}
                        </span>
                      );
                    },
                  },
                  {
                    header: "Target Node",
                    key: "target",
                    render: (_, edge) => {
                      const targetNode = nodesMap[edge.target];
                      return (
                        <span style={{ fontWeight: 600, color: "#0F172A" }}>
                          {targetNode?.name || targetNode?.label || edge.target}
                        </span>
                      );
                    },
                  },
                  {
                    header: "Route Type",
                    key: "relation_type",
                    render: (val, edge) => (
                      <span style={{ color: "#475569" }}>
                        {val ||
                          edge.connections?.[0]?.relation_type ||
                          "network_route"}
                      </span>
                    ),
                  },
                  {
                    header: "Route Status",
                    key: "status",
                    render: (_, edge) => {
                      const sourceNode = nodesMap[edge.source];
                      const targetNode = nodesMap[edge.target];
                      const isActive =
                        sourceNode?.status !== "inactive" &&
                        targetNode?.status !== "inactive";
                      return (
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 12,
                            background: isActive ? "#DCFCE7" : "#FEF3C7",
                            color: isActive ? "#15803D" : "#B45309",
                          }}
                        >
                          {isActive ? "ACTIVE ROUTE" : "DEGRADED"}
                        </span>
                      );
                    },
                  },
                  {
                    header: "Action",
                    key: "action",
                    align: "right",
                    render: (_, edge) => {
                      const targetNode =
                        nodesMap[edge.target] || nodesMap[edge.source];
                      return (
                        <button
                          type="button"
                          onClick={() => handleOpenNodeDetails(targetNode)}
                          style={{
                            padding: "4px 10px",
                            fontSize: 11,
                            fontWeight: 600,
                            background: "#EFF6FF",
                            color: "#1D4ED8",
                            border: "1px solid #BFDBFE",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          Details →
                        </button>
                      );
                    },
                  },
                ]
          }
          data={domain === "applications" ? filteredNodes : filteredEdges}
          keyField="id"
          emptyTitle={
            domain === "applications"
              ? "No applications found"
              : "No network connections found"
          }
          emptySubtitle="No entities match current search or filter criteria."
          emptyIcon={domain === "applications" ? "layers" : "network"}
        />
      )}

      {/* Internal Modals (if not handled by parent callback) */}
      {localSelectedApp && (
        <ApplicationDetailModal
          app={localSelectedApp}
          onClose={() => setLocalSelectedApp(null)}
          onSelectServer={(srv) => {
            setLocalSelectedApp(null);
            setLocalSelectedServer(srv);
          }}
        />
      )}

      {localSelectedServer && (
        <ServerDetailModal
          server={localSelectedServer}
          healthField={healthField}
          onClose={() => setLocalSelectedServer(null)}
        />
      )}
    </div>
  );
}

export default TopologyView;
